import { MultibuyPriceLevel } from '~/api/Catalog';
import { company } from '~/company/Company';

/**
 * Normalizes raw catalog multibuy levels for store instances (Offer/Product).
 *
 * `unitPrice` prefers the company-currency value so every consumer downstream
 * stays in the same currency as the item prices (same pattern as
 * `resolveDisplayPrices`). For converted-price companies levels WITHOUT a
 * converted price are dropped: showing the base-currency amount under the ₽
 * symbol would be worse than not showing the tier at all (e.g. while the
 * backend rollout of `convertedUnitPrice` is in progress).
 */
export function normalizeCatalogMultibuyLevels(
  levels: MultibuyPriceLevel[] | null | undefined,
): MultibuyPriceLevel[] | null {
  if (!levels?.length) {
    return null;
  }

  const usable = company.convertedPricesEnabled
    ? levels.filter((level) => typeof level.convertedUnitPrice === 'number')
    : levels;

  if (!usable.length) {
    return null;
  }

  return usable.map((level, idx) => ({
    id: idx + 1,
    quantity: level.quantity,
    unitPrice: level.convertedUnitPrice ?? level.unitPrice,
  }));
}

/**
 * Returns the highest applicable multibuy level for the given quantity.
 * Levels must be sorted by quantity in ascending order.
 * Returns the level with the maximum quantity, for which quantity >= level.quantity
 */
export function getApplicableMultibuyLevel(
  levels: MultibuyPriceLevel[] | null | undefined,
  quantity: number,
): MultibuyPriceLevel | null {
  if (!levels || levels.length === 0 || quantity <= 0) {
    return null;
  }

  // Find the highest applicable level (with the maximum quantity)
  let applicableLevel: MultibuyPriceLevel | null = null;

  for (const level of levels) {
    if (quantity >= level.quantity) {
      if (!applicableLevel || level.quantity > applicableLevel.quantity) {
        applicableLevel = level;
      }
    }
  }

  return applicableLevel;
}

/**
 * Returns the lowest multibuy level (with the minimum quantity).
 * Used to display information about the minimum quantity for multibuy activation.
 */
export function getLowestMultibuyLevel(
  levels: MultibuyPriceLevel[] | null | undefined,
): MultibuyPriceLevel | null {
  if (!levels || levels.length === 0) {
    return null;
  }

  // Find the level with the minimum quantity
  return levels.reduce(
    (lowest, current) => {
      return !lowest || current.quantity < lowest.quantity ? current : lowest;
    },
    null as MultibuyPriceLevel | null,
  );
}

/**
 * Checks if multibuy is active for the given quantity.
 * Multibuy is active if there is an applicable level for the given quantity.
 */
export function isMultibuyActive(
  levels: MultibuyPriceLevel[] | null | undefined,
  quantity: number,
): boolean {
  return getApplicableMultibuyLevel(levels, quantity) !== null;
}

/**
 * Sorts multibuy levels by quantity in ascending order.
 * This is useful if levels come unsorted from the backend.
 */
export function sortMultibuyLevels(
  levels: MultibuyPriceLevel[] | null | undefined,
): MultibuyPriceLevel[] {
  if (!levels || levels.length === 0) {
    return [];
  }

  return [...levels].sort((a, b) => a.quantity - b.quantity);
}
